import { Injectable } from '@angular/core';
import {
  Router,
  CanActivate,
  RouterEvent,
  NavigationCancel,
  NavigationStart,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { CoverpathControllerInterface } from '../../../../../coverpath-common/angular/interfaces/controller/CoverpathControllerInterface';
import { Location } from '@angular/common';

/**
 * This guard is for preventing user from accessing a given url by typing them in the browser (in my case <host>/invalid-link and <host>/unauthorized urls)
 * These urls should only be routed to by Angular itself if some condition is met while the user is interacting with the web app.
 *
 * In my case:
 * User will be redirect to <host>/invalid-link if they try to access a personalized quote that has been deleted.
 * User will be redirect to <host>/unauthorized if they try to access/fill out an application that has been deleted.
 *
 * Navigate only if navigation happened through angular redirect
 * @source https://github.com/angular/angular/blob/1e9eeafa9e3fbfad5e94d697b8079cf649db626e/packages/router/src/events.ts#L15
 */
@Injectable()
export class RedirectOnlyGuard implements CanActivate {
  controller: CoverpathControllerInterface;

  constructor(private router: Router, private location: Location, readonly amorphicService: AmorphicService) {
    this.controller = amorphicService.controller;
  }

  canActivate() {
    /**
     * Angular increments the navigationId every time a transition is made starting from 1
     * So in order to know whether this route is the first that the user visited when they open the browser
     * (or if they reload the page when this is the active route)
     * We just have to check and make sure that this navigationId is 1.
     * https://github.com/angular/angular/blob/1e9eeafa9e3fbfad5e94d697b8079cf649db626e/packages/router/src/router.ts#L147-L159
     * https://github.com/angular/angular/blob/1e9eeafa9e3fbfad5e94d697b8079cf649db626e/packages/router/src/router.ts#L1087
     */
    const navigationId = this.router.getCurrentNavigation().id;
    const userTypedUrl = navigationId === 1;
    const browserReload = navigationId === 1;
    const backOrForwardPress = this.router.getCurrentNavigation().trigger !== 'imperative';

    if (userTypedUrl || browserReload || backOrForwardPress) {
      this.router.navigate(['home'], { replaceUrl: true });
      return false;
    }
    return true;
  }
}
